/** Query keys, in one place so realtime invalidation and screens agree. */
export const keys = {
  missions: (status: string) => ['missions', status] as const,
  dashboard: (missionId: string) => ['mission', missionId, 'dashboard'] as const,
  // Under the mission: every realtime event of the mission reloads them (ADR 0009).
  missionMap: (missionId: string) => ['mission', missionId, 'map'] as const,
  mapPositions: (missionId: string, bbox: string) =>
    ['mission', missionId, 'map', 'positions', bbox] as const,
  // Comunicazioni: reloaded by the events of the channels only, never by the mission's others.
  comms: (missionId: string) => ['comms', missionId] as const,
  commsChannels: (missionId: string) => ['comms', missionId, 'channels'] as const,
  commsThread: (missionId: string, channelId: string) =>
    ['comms', missionId, 'thread', channelId] as const,
  commsReceipts: (missionId: string, messageId: string) =>
    ['comms', missionId, 'receipts', messageId] as const,
  commsParticipants: (missionId: string) => ['comms', missionId, 'participants'] as const,
  commsStorage: (missionId: string) => ['comms', missionId, 'storage'] as const,
  // Tracce e Replay: trackers and traces reload with the mission's events; a replay does not,
  // it is a look back and refreshes on its own clock while the mission runs.
  trackers: (missionId: string) => ['mission', missionId, 'trackers'] as const,
  tracks: (missionId: string) => ['mission', missionId, 'tracks'] as const,
  replay: (missionId: string, from: string, to: string) => ['replay', missionId, from, to] as const,
  // Report: the overview and each mission's versions; templates apart.
  reports: ['reports'] as const,
  missionReports: (missionId: string) => ['reports', missionId] as const,
  reportTemplates: ['report-templates'] as const,
  // Connettività: reloaded by `station.updated`, never by the mission's events.
  stations: ['stations'] as const,
  meshNodes: ['mesh-nodes'] as const,
  // Sensors of the organization (ADR 0030): not under a mission, reloaded by `device.updated`.
  sensors: ['sensors'] as const,
  loraCoverage: (missionId: string) => ['lora-coverage', missionId] as const,
  // Not under the mission: the weather follows its sources' clock, not the mission's events.
  missionWeather: (missionId: string) => ['weather', missionId] as const,
  windField: (missionId: string, bbox: string) => ['weather', missionId, 'wind', bbox] as const,
  radarFrames: (missionId: string, layer: string) =>
    ['weather', missionId, 'radar', layer] as const,
  events: (missionId: string, kind?: string) =>
    ['mission', missionId, 'events', kind ?? 'all'] as const,
  inventory: (missionId: string, kind: string) => ['mission', missionId, kind] as const,
  // Squadre v7: under the mission, so every event of the mission reloads the board.
  teamBoard: (missionId: string) => ['mission', missionId, 'team-board'] as const,
  // Sentinel Field: under the mission, so a pairing or a revocation (`device.updated`) reloads it.
  fieldPhones: (missionId: string) => ['mission', missionId, 'field-phones'] as const,
  roles: ['roles'] as const,
  permissions: ['permissions'] as const,
  users: (q: string, status: string, page: number) => ['users', q, status, page] as const,
  registryAll: ['registry'] as const,
  registry: (q: string, page: number) => ['registry', q, page] as const,
  modules: ['modules'] as const,
  organization: ['organization'] as const,
  /** The organization's places (ADR 0032): the list of Impostazioni and the suggestions. */
  places: (q: string, kind: string) => ['places', q, kind] as const,
  placeSuggest: (q: string) => ['places', 'suggest', q] as const,
  placesTerritory: ['places', 'territory'] as const,
  notifications: ['notifications'] as const,
  contract: ['organization', 'contract'] as const,
  states: ['states'] as const,
  audit: (action: string, page: number) => ['audit', action, page] as const,
  platformGrants: ['platform-grants'] as const,
  platformOrganizations: ['platform-organizations'] as const,
  organizationGrants: ['organization-grants'] as const,
  sessions: ['sessions'] as const,
  signInMethods: ['sign-in-methods'] as const,
  destinations: ['destinations'] as const,
  drones: ['drones'] as const,
  organizationSmtp: ['organization', 'smtp'] as const,
  organizationThresholds: ['organization', 'thresholds'] as const,
  privacyRequests: (status: string, page: number) => ['privacy', 'requests', status, page] as const,
  retention: ['privacy', 'retention'] as const,
  holds: ['privacy', 'holds'] as const,
  ownIdentities: ['me', 'identities'] as const,
  notificationPreferences: ['me', 'notification-preferences'] as const,
};
